import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Select } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

interface OutputProfile {
  id?: string;
  format?: string;
  colorSpace?: string;
  resolution?: number;
  maxWidth?: number;
  maxHeight?: number;
  includeAlpha?: boolean;
}

const FORMAT_OPTIONS = [
  { value: 'PNG', label: 'PNG' },
  { value: 'JPEG', label: 'JPEG' },
  { value: 'TIFF', label: 'TIFF' },
  { value: 'SVG', label: 'SVG' },
  { value: 'PDF', label: 'PDF' },
];

const COLOR_SPACE_OPTIONS = [
  { value: 'RGB', label: 'RGB' },
  { value: 'CMYK', label: 'CMYK' },
  { value: 'GRAYSCALE', label: 'Grayscale' },
];

export function CatalogOutputTab({ templateId, outputProfile, onDirtyChange }: { templateId: string; outputProfile: Record<string, unknown> | null; onDirtyChange?: (dirty: boolean) => void }) {
  const queryClient = useQueryClient();
  const op = outputProfile as OutputProfile | null;
  const [editing, setEditing] = useState(false);
  const setEditingWithDirty = (value: boolean) => {
    setEditing(value);
    onDirtyChange?.(value);
  };
  const [format, setFormat] = useState(op?.format ?? 'PNG');
  const [colorSpace, setColorSpace] = useState(op?.colorSpace ?? 'RGB');
  const [resolution, setResolution] = useState(String(op?.resolution ?? 300));
  const [maxWidth, setMaxWidth] = useState(op?.maxWidth != null ? String(op.maxWidth) : '');
  const [maxHeight, setMaxHeight] = useState(op?.maxHeight != null ? String(op.maxHeight) : '');
  const [includeAlpha, setIncludeAlpha] = useState(op?.includeAlpha ?? true);

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/catalog/templates/${templateId}/output`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-template', templateId] });
      setEditingWithDirty(false);
      toast('Output profile updated.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update output profile.', 'error');
    },
  });

  const handleSave = () => {
    const body: Record<string, unknown> = { format, colorSpace, resolution: parseInt(resolution), includeAlpha };
    if (maxWidth) body.maxWidth = parseInt(maxWidth);
    if (maxHeight) body.maxHeight = parseInt(maxHeight);
    updateMutation.mutate(body);
  };

  return (
    <Card>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Output Profile</h3>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditingWithDirty(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                setFormat(op?.format ?? 'PNG');
                setColorSpace(op?.colorSpace ?? 'RGB');
                setResolution(String(op?.resolution ?? 300));
                setMaxWidth(op?.maxWidth != null ? String(op.maxWidth) : '');
                setMaxHeight(op?.maxHeight != null ? String(op.maxHeight) : '');
                setIncludeAlpha(op?.includeAlpha ?? true);
                setEditingWithDirty(false);
              }}>Cancel</Button>
            </div>
          )}
        </div>

        {updateMutation.isError && (
          <div className="mb-4 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
            {(updateMutation.error as Error)?.message || 'Failed to update output profile.'}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-text-secondary">Format</label>
            {editing ? (
              <Select value={format} onChange={(e) => setFormat(e.target.value)} options={FORMAT_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.format ?? 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Color Space</label>
            {editing ? (
              <Select value={colorSpace} onChange={(e) => setColorSpace(e.target.value)} options={COLOR_SPACE_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.colorSpace ?? 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Resolution (DPI)</label>
            {editing ? (
              <Input type="number" min="72" max="600" value={resolution} onChange={(e) => setResolution(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.resolution ? `${op.resolution} DPI` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Max Width (px)</label>
            {editing ? (
              <Input type="number" value={maxWidth} onChange={(e) => setMaxWidth(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.maxWidth ? `${op.maxWidth} px` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Max Height (px)</label>
            {editing ? (
              <Input type="number" value={maxHeight} onChange={(e) => setMaxHeight(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.maxHeight ? `${op.maxHeight} px` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Include Alpha</label>
            {editing ? (
              <label className="mt-1 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={includeAlpha} onChange={(e) => setIncludeAlpha(e.target.checked)} className="rounded border-border" />
                Transparent background support
              </label>
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{op?.includeAlpha ? 'Yes' : 'No'}</p>
            )}
          </div>
        </div>

        {!op && !editing && (
          <div className="mt-6 rounded-lg border border-dashed border-border p-8 text-center">
            <svg className="mx-auto h-10 w-10 text-text-tertiary/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <p className="mt-3 text-sm font-medium text-text-secondary">Output profile not configured</p>
            <p className="mt-1 text-xs text-text-tertiary">Click Edit to set up output settings for this template.</p>
          </div>
        )}
      </div>
    </Card>
  );
}
